import {
  DatabaseTableName,
  SupabaseTableHookQueryOperators
} from '@dolfin/business/custom_types'
import { removeAccents } from '@dolfin/business/utils'
import type {
  PostgrestClientOptions,
  PostgrestFilterBuilder
} from '@supabase/postgrest-js'
import { format } from 'date-fns'
import { isArrayLike, isNumber } from 'lodash'

type GenericSchema = {
  Tables: Record<
    string,
    {
      Row: Record<string, unknown>
      Insert: Record<string, unknown>
      Update: Record<string, unknown>
      Relationships: Array<{
        foreignKeyName: string
        columns: string[]
        referencedRelation: string
        referencedColumns: string[]
        isOneToOne?: boolean
      }>
    }
  >
  Views: Record<
    string,
    {
      Row: Record<string, unknown>
      Relationships: Array<{
        foreignKeyName: string
        columns: string[]
        referencedRelation: string
        referencedColumns: string[]
        isOneToOne?: boolean
      }>
      Insert?: Record<string, unknown>
      Update?: Record<string, unknown>
    }
  >
  Functions: Record<
    string,
    {
      Args: Record<string, unknown> | never
      Returns: unknown
    }
  >
}

export const getPkeyForTable = (tableName: DatabaseTableName) => {
  switch (tableName) {
    case 'deals':
      return 'deal_id'
    case 'activities':
      return 'activity_id'
    case 'invoices':
      return 'invoice_id'
    case 'line_items':
      return 'line_item_id'
    case 'custom_records':
      return 'record_id'
    case 'targets':
      return 'target_id'
    case 'profiles':
      return 'email'
    default:
      return 'id'
  }
}

export const ORG_FILTERS = [
  'activities',
  'csv_mappers',
  'custom_fields',
  'custom_record_tables',
  'custom_records',
  'deals',
  'invitations',
  'invoices',
  'compensation_rule_comments',
  'kpis',
  'kpis_tables',
  'metrics',
  'payouts',
  'plans',
  'profile_org_variable_salary',
  'statements',
  'targets',
  'table_config',
  'teams',
  'validation_steps'
]

export const buildQuery = <
  Schema extends GenericSchema,
  Row extends Record<string, unknown>,
  Result
>(
  query: PostgrestFilterBuilder<PostgrestClientOptions, Schema, Row, Result>,
  options: {
    selectedOrganizationId: number
    tableName: string
    skipOrgFilter?: boolean
  },
  filtersAndSorting: SupabaseTableHookQueryOperators
) => {
  type QueryBuilder = typeof query
  type EqColumn = Parameters<QueryBuilder['eq']>[0]
  type EqValue = Parameters<QueryBuilder['eq']>[1]
  type NeqColumn = Parameters<QueryBuilder['neq']>[0]
  type NeqValue = Parameters<QueryBuilder['neq']>[1]
  type IsColumn = Parameters<QueryBuilder['is']>[0]
  type IsValue = Parameters<QueryBuilder['is']>[1]
  type GtColumn = Parameters<QueryBuilder['gt']>[0]
  type GtValue = Parameters<QueryBuilder['gt']>[1]
  type LtColumn = Parameters<QueryBuilder['lt']>[0]
  type LtValue = Parameters<QueryBuilder['lt']>[1]
  type InColumn = Parameters<QueryBuilder['in']>[0]
  type InValues = Parameters<QueryBuilder['in']>[1]
  type ContainsColumn = Parameters<QueryBuilder['contains']>[0]
  type ContainsValue = Parameters<QueryBuilder['contains']>[1]
  type ILikeColumn = Parameters<QueryBuilder['ilike']>[0]
  type ILikeValue = Parameters<QueryBuilder['ilike']>[1]
  type GteColumn = Parameters<QueryBuilder['gte']>[0]
  type GteValue = Parameters<QueryBuilder['gte']>[1]
  type LteColumn = Parameters<QueryBuilder['lte']>[0]
  type LteValue = Parameters<QueryBuilder['lte']>[1]
  type FilterColumn = Parameters<QueryBuilder['filter']>[0]
  type FilterOperator = Parameters<QueryBuilder['filter']>[1]
  type FilterValue = Parameters<QueryBuilder['filter']>[2]
  type TextSearchColumn = Parameters<QueryBuilder['textSearch']>[0]
  type TextSearchQuery = Parameters<QueryBuilder['textSearch']>[1]
  type RangeLtColumn = Parameters<QueryBuilder['rangeLt']>[0]
  type RangeLtValue = Parameters<QueryBuilder['rangeLt']>[1]
  type RangeGtColumn = Parameters<QueryBuilder['rangeGt']>[0]
  type RangeGtValue = Parameters<QueryBuilder['rangeGt']>[1]
  type OverlapsColumn = Parameters<QueryBuilder['overlaps']>[0]
  type OverlapsValue = Parameters<QueryBuilder['overlaps']>[1]

  const {
    order,
    range,
    eqFilters,
    neqFilters,
    isFilters,
    gtFilters,
    ltFilters,
    inFilters,
    containsFilters,
    ilikeFilters,
    timeRange,
    orFilter,
    orFilters,
    notFilter,
    notFilters,
    filters,
    limit,
    textSearch,
    rangeLt,
    rangeGt,
    overlaps
  } = filtersAndSorting

  if (order && Array.isArray(order)) {
    order.forEach(o => {
      query = query.order(o.column, o.options)
    })
  } else {
    query = query.order('created_at', { ascending: false })
  }

  if (
    options.selectedOrganizationId &&
    ORG_FILTERS.includes(options.tableName) &&
    !options.skipOrgFilter
  ) {
    query = query.eq(
      'organization_id' as EqColumn,
      options.selectedOrganizationId as unknown as EqValue
    )
  }

  if (range && isNumber(range?.from) && isNumber(range?.to)) {
    query = query.range(range.from, range.to)
  }

  if (eqFilters) {
    eqFilters.forEach(filter => {
      query = query.eq(
        filter.column as EqColumn,
        filter.value as unknown as EqValue
      )
    })
  }

  if (neqFilters) {
    neqFilters.forEach(filter => {
      query = query.neq(
        filter.column as NeqColumn,
        filter.value as unknown as NeqValue
      )
    })
  }

  if (isFilters) {
    isFilters.forEach(filter => {
      if (filter.negate) {
        query = query.not(filter.column, 'is', filter.value)
      } else {
        query = query.is(
          filter.column as IsColumn,
          filter.value as unknown as IsValue
        )
      }
    })
  }

  if (gtFilters) {
    gtFilters.forEach(filter => {
      if (filter.orEqual) {
        query = query.gte(filter.column, filter.value)
      } else {
        query = query.gt(
          filter.column as GtColumn,
          filter.value as unknown as GtValue
        )
      }
    })
  }

  if (ltFilters) {
    ltFilters.forEach(filter => {
      if (filter.orEqual) {
        query = query.lte(filter.column, filter.value)
      } else {
        query = query.lt(
          filter.column as LtColumn,
          filter.value as unknown as LtValue
        )
      }
    })
  }

  if (inFilters) {
    inFilters.forEach(filter => {
      query = query.in(
        filter.column as InColumn,
        filter.values as unknown as InValues
      )
    })
  }

  if (containsFilters) {
    containsFilters.forEach(filter => {
      query = query.contains(
        filter.column as ContainsColumn,
        filter.values as unknown as ContainsValue
      )
    })
  }

  if (ilikeFilters) {
    ilikeFilters.forEach(filter => {
      query = query.ilike(
        filter.column as ILikeColumn,
        filter.value as unknown as ILikeValue
      )
    })
  }

  if (timeRange) {
    const { to, from, selectValue } = timeRange

    if (from) {
      query = query.gte(
        (selectValue ?? 'created_at') as GteColumn,
        format(from, 'yyyy-MM-dd') as unknown as GteValue
      )
    }

    if (to) {
      query = query.lte(
        (selectValue ?? 'created_at') as LteColumn,
        format(to, 'yyyy-MM-dd') as unknown as LteValue
      )
    }
  }

  if (orFilter) {
    query = query.or(
      typeof orFilter === 'string' ? orFilter : orFilter[0],
      typeof orFilter === 'string' ? undefined : orFilter[1]
    )
  }

  // Multiple OR filters - each .or() call is ANDed together by PostgREST
  // This allows NULL-safe excludes: (col IS NULL OR col NOT IN (...)) AND (col2 IS NULL OR col2 NOT IN (...))
  if (orFilters) {
    orFilters.forEach(filter => {
      query = query.or(
        typeof filter === 'string' ? filter : filter[0],
        typeof filter === 'string' ? undefined : filter[1]
      )
    })
  }

  if (notFilter) {
    query = query.not(...notFilter)
  }

  notFilters?.forEach(filter => {
    query = query.not(...filter)
  })

  if (filters) {
    if (Array.isArray(filters)) {
      filters.forEach(f => {
        query = query.filter(
          f.column as FilterColumn,
          f.operator as FilterOperator,
          f.value as unknown as FilterValue
        )
      })
    } else {
      query = query.filter(
        filters.column as FilterColumn,
        filters.operator as FilterOperator,
        filters.value as unknown as FilterValue
      )
    }
  }

  if (limit) {
    query = query.limit(limit)
  }

  if (textSearch) {
    query = query.textSearch(
      textSearch.column as TextSearchColumn,
      removeAccents(textSearch.query) as unknown as TextSearchQuery,
      textSearch.options
    )
  }

  if (rangeLt) {
    query = query.rangeLt(
      rangeLt.column as RangeLtColumn,
      rangeLt.value as unknown as RangeLtValue
    )
  }

  if (rangeGt) {
    query = query.rangeGt(
      rangeGt.column as RangeGtColumn,
      rangeGt.value as unknown as RangeGtValue
    )
  }

  if (overlaps) {
    query = query.overlaps(
      overlaps.column as OverlapsColumn,
      overlaps.value as unknown as OverlapsValue
    )
  }
  return query
}

export async function* getAllPaginatedPages<
  Schema extends GenericSchema,
  Row extends Record<string, unknown>,
  Result extends Iterable<Row>
>(
  query: PostgrestFilterBuilder<PostgrestClientOptions, Schema, Row, Result>,
  start: number = 0,
  end: number = Infinity,
  step: number = 1000
) {
  let _start = start
  let _end = Math.min(_start + step - 1, end)
  while (_start <= end) {
    const { data, error } = await query.range(_start, _end)
    if (error) throw error
    if (!isArrayLike(data)) throw new Error('Data is not array-like')
    if (data.length === 0) break
    yield data
    _start += step
    _end = Math.min(_end + step, end)
  }
}

export async function* getAllPaginated<
  Schema extends GenericSchema,
  Row extends Record<string, unknown>,
  Result extends Iterable<Row>
>(
  query: PostgrestFilterBuilder<PostgrestClientOptions, Schema, Row, Result>,
  start: number = 0,
  end: number = Infinity,
  step: number = 1000
) {
  for await (const page of getAllPaginatedPages(query, start, end, step)) {
    for (const row of page) {
      yield row
    }
  }
}
